import { useId } from "react";
import { Link, useParams } from "react-router";
import { ApiError } from "@/lib/api";
import type { EmployeeDetailResponse } from "@/lib/staff-api";
import { formatCurrency, formatDate } from "@/lib/formatters";
import { badgeExpiry, formatCalendarDate, STATUS_LABELS } from "@/lib/roster";
import { staffCan } from "@/lib/staff-capabilities";
import { useStaffEmployee } from "@/lib/staff-hooks";
import { TRACK_LABELS } from "@/lib/staff-timeline";
import { useStaffAuth } from "@/lib/use-staff-auth";
import { CompanyLink } from "./CompanyLink";
import { Loaded } from "./Loaded";
import { OrdersTable } from "./OrdersTable";
import { MoreActions, PrimaryAction } from "./QueueRow";
import { Timeline } from "./Timeline";

const BACK = "text-sm text-blue-700 hover:text-blue-900 hover:underline";
const MARKER = "rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700";

function Fact({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-600">{label}</dt>
      <dd className="mt-1">{value}</dd>
    </div>
  );
}

function EmployeeBody({ data, canAct }: { data: EmployeeDetailResponse; canAct: boolean }): React.ReactElement {
  const factsId = useId();
  const historyId = useId();
  const ordersId = useId();
  const { employee, orders, timeline, historyStartsOn } = data;
  const expiry = badgeExpiry(employee.clearanceDate);
  return (
    <div className="space-y-8">
      {employee.vendorHidden && (
        <div role="status" className="rounded-md border border-gray-400 bg-gray-100 px-4 py-3 text-sm text-gray-900">
          Hidden — only IT can see this company.
        </div>
      )}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold text-gray-900">
            {employee.firstName} {employee.lastName}
          </h1>
          <p className="text-sm text-gray-700">
            <CompanyLink vendorId={employee.vendorId} vendor={employee.vendor} />
          </p>
          <p className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
            <span>{STATUS_LABELS[employee.status]}</span>
            {employee.inactive && <span className={MARKER}>No longer employed</span>}
          </p>
        </div>
        {canAct && (
          <div className="flex flex-wrap items-start gap-3">
            {!employee.inactive && <PrimaryAction employee={employee} />}
            <MoreActions employee={employee} />
          </div>
        )}
      </div>

      <section aria-labelledby={factsId} className="space-y-3">
        <h2 id={factsId} className="text-lg font-semibold text-gray-900">
          Facts
        </h2>
        <dl className="grid grid-cols-1 gap-4 rounded-lg border border-gray-200 bg-white p-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
          <Fact label="Track" value={employee.track ? TRACK_LABELS[employee.track] : "—"} />
          <Fact label="Clearance date" value={employee.clearanceDate ? formatCalendarDate(employee.clearanceDate) : "—"} />
          <Fact label="Badge expires" value={expiry ? formatCalendarDate(expiry) : "—"} />
          <Fact label="Photo on file" value={employee.photoOnFile ? "Yes" : "No"} />
          <Fact label="Added" value={formatDate(employee.createdAt)} />
          {typeof employee.completionPriceOwed === "number" && (
            <Fact label="Completion owed" value={formatCurrency(employee.completionPriceOwed)} />
          )}
        </dl>
      </section>

      <section aria-labelledby={historyId} className="space-y-3">
        <h2 id={historyId} className="text-lg font-semibold text-gray-900">
          History
        </h2>
        <Timeline entries={timeline} startsOn={historyStartsOn} />
      </section>

      <section aria-labelledby={ordersId} className="space-y-3">
        <h2 id={ordersId} className="text-lg font-semibold text-gray-900">
          Orders
        </h2>
        {orders.length === 0 ? <p className="text-sm text-gray-600">No orders on file.</p> : <OrdersTable rows={orders} />}
      </section>
    </div>
  );
}

/** One employee: facts, the stage's action, their history and their orders.
 *  Accounting reads; Mark and IT can act. */
export function StaffEmployeePage(): React.ReactElement {
  const { vendorId = "", id = "" } = useParams();
  const { staff } = useStaffAuth();
  const isIt = staff !== null && staffCan(staff.role, "manage_staff");
  const canAct = staff !== null && staffCan(staff.role, "work_queues");
  // IT must reach a hidden company's employee, as on the company page.
  const params = new URLSearchParams(isIt ? { includeHidden: "true" } : {});
  const query = useStaffEmployee(vendorId, id, params);
  const notFound = query.error instanceof ApiError && query.error.status === 404;
  if (notFound) {
    return (
      <div className="space-y-6">
        <Link to="/staff/companies" className={BACK}>
          ← Companies
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Employee not found</h1>
      </div>
    );
  }
  return (
    // Keyed by employee: an action error on one employee must not follow to the next.
    <Loaded queries={[query]}>{() => <EmployeeBody key={`${vendorId}/${id}`} data={query.data!} canAct={canAct} />}</Loaded>
  );
}
